<template>
  <div>
    <div
      v-if="canShowIntegrationMonitoring()"
      class="integration-monitoring-page"
    >
      <span
        class="primary--text font-weight-bold"
        style="font-size: 24px;">
        {{ $t('integrationMonitoring.title') }}
      </span>

      <div class="d-flex flex-row mt-5">
        <IntegrationMonitoringFilter />
      </div>

      <div class="my-5">
        <IntegrationMonitoringSummaryCards
          :cards="summaryCards"
          :loading="loadingPage || loadingTable"
          :active-card="activeSummaryCard"
          @card-click="onCardClick"
        />
      </div>

      <div
        v-if="showTable"
        class="mb-5"
      >
        <IntegrationMonitoringTable />
      </div>

      <div class="integration-monitoring-page__footer">
        <DefaultFooter />
      </div>
    </div>

    <SnackbarCustomize ref="IntegrationMonitoringSnackbar" />
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import Authorities from '@/shared/enums/authorities';
import AuthorityManagementMixin from '@/shared/mixins/authorityManagement/authorityManagementMixin';
import GenericFunctionsMixin from '@/shared/mixins/genericFunctions/genericFunctionsMixin';
import DefaultFooter from '@/components/Default/DefaultFooter/DefaultFooter.vue';
import IntegrationMonitoringFilter from '@/components/IntegrationMonitoring/IntegrationMonitoringFilter.vue';
import IntegrationMonitoringSummaryCards from '@/components/IntegrationMonitoring/IntegrationMonitoringSummaryCards.vue';
import IntegrationMonitoringTable from '@/components/IntegrationMonitoring/IntegrationMonitoringTable.vue';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import {
  CADASTRAL_INVALID_REQUEST_STATUS,
  CADASTRAL_POSITION_PROCESS_TYPE,
} from '@/store/modules/integration-monitoring/integration-monitoring-config';

const EMPTY_INDICATOR_VALUE = '—';
const ERROR_STATUS = 'COMPLETED_WITH_ERRORS';

export default {
  name: 'IntegrationMonitoring',
  components: {
    DefaultFooter,
    IntegrationMonitoringFilter,
    IntegrationMonitoringSummaryCards,
    IntegrationMonitoringTable,
    SnackbarCustomize,
  },
  mixins: [AuthorityManagementMixin, GenericFunctionsMixin],
  data: () => ({
    hasShownNoPermissionSnackbar: false,
  }),
  computed: {
    ...mapGetters({
      showSnackbarCustomize: 'integration-monitoring/showSnackbarCustomize',
      indicators: 'integration-monitoring/indicators',
      filter: 'integration-monitoring/filter',
      loadingPage: 'integration-monitoring/loadingPage',
      loadingTable: 'integration-monitoring/loadingTable',
      showTable: 'integration-monitoring/showTable',
    }),
    periodLabel() {
      const start = this.filter?.periodStart;
      const end = this.filter?.periodEnd;
      if (!start || !end) return null;

      const startDay = moment(start).startOf('day');
      const endDay = moment(end).startOf('day');
      if (!startDay.isValid() || !endDay.isValid()) return null;

      const days = endDay.diff(startDay, 'days') + 1;
      if (days < 1) return null;
      if (days === 1) {
        return this.$t('integrationMonitoring.indicators.periodDay');
      }

      return this.$t('integrationMonitoring.indicators.periodDays', { n: days });
    },
    summaryCards() {
      return [
        {
          key: 'totalRequests',
          value: this.indicators?.totalRequests ?? EMPTY_INDICATOR_VALUE,
          label: this.$t('integrationMonitoring.indicators.totalRequests'),
          color: 'var(--v-success-base)',
          icon: 'mdi-inbox-arrow-down',
          formatAsNumber: true,
          periodLabel: this.periodLabel,
        },
        {
          key: 'totalWithErrors',
          value: this.indicators?.totalWithErrors ?? EMPTY_INDICATOR_VALUE,
          label: this.$t('integrationMonitoring.indicators.totalWithErrors'),
          color: 'var(--v-error-base)',
          icon: 'mdi-alert-circle-outline',
          formatAsNumber: true,
        },
      ];
    },
    activeSummaryCard() {
      const status = this.filter?.status || [];
      if (status.includes(ERROR_STATUS) || status.includes(CADASTRAL_INVALID_REQUEST_STATUS)) {
        return 'totalWithErrors';
      }
      if (!status.length) return 'totalRequests';
      return null;
    },
  },
  watch: {
    showSnackbarCustomize: {
      handler(snackbar) {
        if (snackbar && snackbar.show) {
          const { type, message, timeout } = snackbar;
          this.$refs.IntegrationMonitoringSnackbar.open(type, message, timeout);
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      initPage: 'integration-monitoring/initPage',
      showSnackbar: 'integration-monitoring/showSnackbar',
      applyFilter: 'integration-monitoring/applyFilter',
      searchByFilter: 'integration-monitoring/searchByFilter',
    }),
    async onCardClick(key) {
      if (this.activeSummaryCard === key) {
        this.applyFilter({ status: [] });
      } else if (key === 'totalWithErrors') {
        const errorStatus = this.filter?.processType === CADASTRAL_POSITION_PROCESS_TYPE
          ? CADASTRAL_INVALID_REQUEST_STATUS
          : ERROR_STATUS;
        this.applyFilter({ status: [errorStatus] });
      } else {
        this.applyFilter({ status: [] });
      }

      await this.searchByFilter();
    },
    hasViewPermission() {
      return this.hasPermission(Authorities.viewIntegrationMonitoring);
    },
    canShowIntegrationMonitoring() {
      if (this.hasViewPermission()) {
        this.hasShownNoPermissionSnackbar = false;
        return true;
      }

      if (!this.hasShownNoPermissionSnackbar) {
        this.showSnackbar({
          type: 'error',
          message: String(this.$t('integrationMonitoring.messages.noPermission')),
          show: true,
        });
        this.hasShownNoPermissionSnackbar = true;
      }

      return false;
    },
  },
  async created() {
    if (!this.hasViewPermission()) return;
    await this.initPage();
  },
};
</script>

<style lang="scss">
.integration-monitoring-page {
  margin: 20px;
  min-height: calc(100vh - 280px);

  &__footer {
    padding-bottom: 12px;
    padding-top: 12px;
    width: 100%;
  }
}
</style>
